import { Injectable, computed } from '@angular/core'
import { BaseSignalStore } from './base-store'
import { Notification, NotificationState, NotificationFilter } from '@shared/models/notification/notification.models'

/**
 * Notifications Store
 *
 * Manages notification state using Angular signals for reactive state management.
 * Provides centralized store for real-time notifications received via SignalR.
 *
 * Features:
 * - Reactive state management with signals
 * - Notification list management
 * - Unread notification tracking
 * - Connection status management
 * - Filtering and searching capabilities
 */
@Injectable({ providedIn: 'root' })
export class NotificationsStore extends BaseSignalStore<NotificationState> {
  constructor() {
    super({
      notifications: [],
      unreadNotifications: [],
      unreadCount: 0,
      isConnected: false,
      isConnecting: false,
      connectionError: null,
      lastNotificationAt: null,
    })
  }

  // Computed selectors
  readonly unreadCountSignal = computed(() => this.state().unreadCount)
  readonly isConnectedSignal = computed(() => this.state().isConnected)
  readonly hasUnreadSignal = computed(() => this.state().unreadCount > 0)
  readonly notificationsSignal = computed(() => this.state().notifications)
  readonly unreadNotificationsSignal = computed(() => this.state().unreadNotifications)

  /**
   * Adds a new notification to the store.
   * @returns true if the notification was added, false if it was a duplicate.
   */
  addNotification(notification: Notification): boolean {
    const currentState = this.state()

    // Deduplicate: backend sends via User() AND Group() — same notification can arrive twice
    if (currentState.notifications.some(n => n.id === notification.id)) {
      return false
    }

    const notifications = [notification, ...currentState.notifications]
    const unreadNotifications = notification.readAt
      ? currentState.unreadNotifications
      : [notification, ...currentState.unreadNotifications]
    const unreadCount = unreadNotifications.length

    this.setState({
      notifications,
      unreadNotifications,
      unreadCount,
      lastNotificationAt: new Date(),
    })

    return true
  }

  /**
   * Marks a notification as read
   * @param notificationId Notification ID to mark as read
   */
  markAsRead(notificationId: string): void {
    const currentState = this.state()
    const notifications = currentState.notifications.map(n =>
      n.id === notificationId ? { ...n, readAt: new Date().toISOString() } : n
    )
    const unreadNotifications = notifications.filter(n => !n.readAt)
    const unreadCount = unreadNotifications.length

    this.setState({
      notifications,
      unreadNotifications,
      unreadCount,
    })
  }

  /**
   * Marks all notifications as read
   */
  markAllAsRead(): void {
    const currentState = this.state()
    const now = new Date().toISOString()
    const notifications = currentState.notifications.map(n => ({
      ...n,
      readAt: n.readAt || now,
    }))
    const unreadNotifications: Notification[] = []
    const unreadCount = 0

    this.setState({
      notifications,
      unreadNotifications,
      unreadCount,
    })
  }

  /**
   * Removes a notification from the store
   * @param notificationId Notification ID to remove
   */
  removeNotification(notificationId: string): void {
    const currentState = this.state()
    const notifications = currentState.notifications.filter(n => n.id !== notificationId)
    const unreadNotifications = notifications.filter(n => !n.readAt)
    const unreadCount = unreadNotifications.length

    this.setState({
      notifications,
      unreadNotifications,
      unreadCount,
    })
  }

  /**
   * Clears all notifications
   */
  clearAll(): void {
    this.setState({
      notifications: [],
      unreadNotifications: [],
      unreadCount: 0,
      lastNotificationAt: null,
    })
  }

  /**
   * Sets the connection status
   * @param isConnected Whether SignalR is connected
   * @param isConnecting Whether connection is in progress
   * @param error Connection error if any
   */
  setConnectionStatus(isConnected: boolean, isConnecting: boolean = false, error: string | null = null): void {
    this.setState({
      isConnected,
      isConnecting,
      connectionError: error,
    })
  }

  /**
   * Filters notifications based on criteria
   * @param filter Filter criteria
   * @returns Filtered notifications
   */
  getFilteredNotifications(filter: NotificationFilter): Notification[] {
    const currentState = this.state()
    let notifications = filter.unreadOnly ? currentState.unreadNotifications : currentState.notifications

    if (filter.category !== undefined) {
      notifications = notifications.filter(n => n.category === filter.category)
    }

    if (filter.severity !== undefined) {
      notifications = notifications.filter(n => n.severity === filter.severity)
    }

    if (filter.type !== undefined) {
      notifications = notifications.filter(n => n.type === filter.type)
    }

    if (filter.successionFileId !== undefined) {
      notifications = notifications.filter(n => n.successionFileId === filter.successionFileId)
    }

    if (filter.searchTerm) {
      const searchLower = filter.searchTerm.toLowerCase()
      notifications = notifications.filter(
        n => n.title?.toLowerCase().includes(searchLower) || n.message?.toLowerCase().includes(searchLower)
      )
    }

    return notifications
  }

  /**
   * Gets notification by ID
   * @param notificationId Notification ID
   * @returns Notification or undefined
   */
  getNotificationById(notificationId: string): Notification | undefined {
    return this.state().notifications.find(n => n.id === notificationId)
  }

  /**
   * Gets unread count for a specific succession file
   * @param successionFileId Succession file ID
   * @returns Unread count
   */
  getUnreadCountForFile(successionFileId: number): number {
    return this.state().unreadNotifications.filter(n => n.successionFileId === successionFileId).length
  }
}
